import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatCurrency } from '@/lib/utils';

type Totals = { subtotal: number; discountAmount: number; taxAmount: number; total: number };
export interface FinancialReview {
  orderNumber: string;
  currency: string;
  before: Totals;
  after: Totals;
  token: string;
  requiresReason: boolean;
  invoices: Array<{
    id: string;
    invoiceNumber: string;
    currency: string;
    kind: string;
    before: Totals;
    after: Totals;
    paidAmount: number;
    oldBalanceDue: number;
    balanceDue: number;
  }>;
  accounting: Array<{
    deliveryId: string;
    date: Date;
    replacesPostedEntry: boolean;
    beforeTotalTRY?: number | null;
    afterTotalTRY?: number;
  }>;
  stockChanges: Array<{ description: string; quantity: number; action: 'reserve' | 'release' }>;
  detailsChanges?: Array<{ field: string; before: string | null; after: string | null }>;
}

export function FinancialCorrectionDialog({
  review,
  pending,
  onClose,
  onConfirm,
  confirmLabel,
}: {
  review: FinancialReview | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  confirmLabel?: string;
}) {
  const { t } = useTranslation('trading');
  const [reason, setReason] = useState('');
  useEffect(() => {
    setReason('');
  }, [review?.token]);
  const prefix = 'sales.correction';
  const totals = (before: Totals, after: Totals, currency: string) => (
    <table className="w-full text-sm">
      <thead>
        <tr>
          <th className="text-left">{t(`${prefix}.amount`)}</th>
          <th className="text-right">{t(`${prefix}.before`)}</th>
          <th className="text-right">{t(`${prefix}.after`)}</th>
        </tr>
      </thead>
      <tbody>
        {(['subtotal', 'discountAmount', 'taxAmount', 'total'] as const).map((key) => (
          <tr key={key}>
            <td className="py-1">{t(`${prefix}.${key}`)}</td>
            <td className="text-right">{formatCurrency(before[key], currency)}</td>
            <td className="text-right font-medium">{formatCurrency(after[key], currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Dialog
      open={!!review}
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
    >
      <DialogContent
        className="max-w-2xl"
        onEscapeKeyDown={(event) => {
          if (pending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (pending) event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{t(`${prefix}.title`)}</DialogTitle>
          <DialogDescription>{t(`${prefix}.description`)}</DialogDescription>
        </DialogHeader>
        {review && (
          <div className="space-y-5">
            <section>
              <h3 className="mb-2 font-medium">{review.orderNumber}</h3>
              {totals(review.before, review.after, review.currency)}
            </section>
            {review.detailsChanges?.length ? (
              <section className="space-y-2">
                <h3 className="font-medium">{t(`${prefix}.details`)}</h3>
                {review.detailsChanges.map((change) => (
                  <div
                    key={change.field}
                    className="grid gap-1 border-b pb-2 text-sm sm:grid-cols-3"
                  >
                    <span>
                      {t([`finance.invoices.${change.field}`, `sales.orders.${change.field}`], {
                        defaultValue: change.field,
                      })}
                    </span>
                    <span className="text-muted-foreground break-words">
                      {change.before || '—'}
                    </span>
                    <span className="break-words">{change.after || '—'}</span>
                  </div>
                ))}
              </section>
            ) : null}
            {review.invoices.map((invoice) => (
              <section key={invoice.id} className="rounded border p-3">
                <h3 className="mb-2 font-medium">
                  {invoice.invoiceNumber}
                  {invoice.kind === 'partial' && (
                    <span className="text-muted-foreground ml-2 text-sm">
                      {t(`${prefix}.partial`)}
                    </span>
                  )}
                </h3>
                {totals(invoice.before, invoice.after, invoice.currency)}
                <div className="mt-2 flex flex-wrap justify-between gap-2 border-t pt-2 text-sm">
                  <span>
                    {t(`${prefix}.paid`)}: {formatCurrency(invoice.paidAmount, invoice.currency)}
                  </span>
                  <span>
                    {t(`${prefix}.balance`)}:{' '}
                    {formatCurrency(invoice.oldBalanceDue, invoice.currency)} →{' '}
                    {formatCurrency(invoice.balanceDue, invoice.currency)}
                  </span>
                </div>
              </section>
            ))}
            {review.accounting.length > 0 && (
              <section className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  {t(`${prefix}.accounting`, { count: review.accounting.length })}
                </p>
                {review.accounting.map((entry) => (
                  <div className="flex flex-wrap justify-between gap-2" key={entry.deliveryId}>
                    <span>{new Date(entry.date).toLocaleDateString()}</span>
                    <span>
                      {entry.beforeTotalTRY != null
                        ? formatCurrency(entry.beforeTotalTRY, 'TRY')
                        : '—'}{' '}
                      →{' '}
                      {entry.afterTotalTRY != null
                        ? formatCurrency(entry.afterTotalTRY, 'TRY')
                        : '—'}
                    </span>
                  </div>
                ))}
              </section>
            )}
            {review.stockChanges.length > 0 && (
              <section>
                <h3 className="mb-1 font-medium">{t(`${prefix}.stock`)}</h3>
                <ul className="list-inside list-disc text-sm">
                  {review.stockChanges.map((change, index) => (
                    <li key={index}>
                      {t(`${prefix}.${change.action}`)} {change.quantity} × {change.description}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            <p className="text-muted-foreground text-sm">{t(`${prefix}.preserved`)}</p>
            <div className="space-y-2">
              <Label htmlFor="correction-reason">
                {t(`${prefix}.reason`)}
                {review.requiresReason ? ' *' : ''}
              </Label>
              <Textarea
                id="correction-reason"
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                maxLength={2000}
                disabled={pending}
                placeholder={t(`${prefix}.reasonPlaceholder`)}
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={pending} onClick={onClose}>
            {t(`${prefix}.back`)}
          </Button>
          <Button
            className="h-auto min-h-10 max-w-full whitespace-normal py-2"
            disabled={pending || !review || (review.requiresReason && !reason.trim())}
            onClick={() => onConfirm(reason.trim())}
          >
            {pending ? t('common.saving') : confirmLabel || t(`${prefix}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
